'use client'

import { useState, useEffect } from 'react'
import Bowser from 'bowser'

/** Pure tablet detection shared by the tablet gate and touchscreen mode. */
export function detectTabletDevice(userAgent: string, maxTouchPoints: number): boolean {
  const platformType = Bowser.getParser(userAgent).getPlatformType()
  const isIpad = /Macintosh/.test(userAgent) && maxTouchPoints > 1
  return platformType === 'tablet' || isIpad
}

/**
 * UA-based tablet *device* detection, sibling to {@link useIsMobileDevice}.
 *
 * True for tablets only; phones and desktops return false. This covers the
 * iPadOS 13+ gotcha: modern iPads report a desktop ("Macintosh") user-agent,
 * so Bowser alone classifies them as desktop. We detect that case via touch
 * support (maxTouchPoints) on a Mac UA — a trackpad Mac reports
 * maxTouchPoints === 0, a touch iPad reports > 1.
 *
 * Like useIsMobileDevice this never changes on window resize — a narrow desktop
 * window is not a tablet. Use it for the project-page device gate alongside
 * useIsMobileDevice (phones) and useIsMobile (viewport, for layout).
 *
 * SSR-safe: returns false during SSR/hydration, then the real value once
 * mounted; stable for the rest of the session.
 */
export function useIsTablet(): boolean {
  const [isTablet, setIsTablet] = useState(false)

  useEffect(() => {
    // SSR-safe device detection: start false on the server + first client render,
    // then flip once mounted. This necessarily sets state from the effect.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsTablet(detectTabletDevice(navigator.userAgent, navigator.maxTouchPoints))
  }, [])

  return isTablet
}
